import { Navigate, useLocation } from 'react-router-dom';
import {
  useAuth,
  isInternalRole,
  isCrewRole,
  isClientRole,
  isSalesRole,
  getDefaultRoute,
} from '@/auth/AuthContext';

interface RouteGuardProps {
  children: React.ReactNode;
  allowed: 'internal' | 'crew' | 'client' | 'sales' | 'any';
  /** When set, user must also hold at least one of these permissions. */
  anyPermission?: readonly string[];
}

export function RouteGuard({ children, allowed, anyPermission }: RouteGuardProps) {
  const { user, loading, hasAnyPermission } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="loading-center" style={{ minHeight: '100dvh' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Accounts still on a temporary password must set their own before anything else.
  if (user.forcePasswordReset && location.pathname !== '/account/password') {
    return <Navigate to="/account/password" replace />;
  }

  const role = user.role;
  let permitted = false;

  switch (allowed) {
    case 'internal':
      permitted = isInternalRole(role) || role === 'Admin';
      break;
    case 'crew':
      permitted = isCrewRole(role);
      break;
    case 'client':
      permitted = isClientRole(role);
      break;
    case 'sales':
      // Sales shell only — internal staff use Dispatch/Admin nav, not the Sales chrome.
      permitted = isSalesRole(role);
      break;
    case 'any':
      permitted = true;
      break;
  }

  if (permitted && anyPermission?.length) {
    permitted = hasAnyPermission(anyPermission);
  }

  if (!permitted) {
    return <Navigate to={getDefaultRoute(role, user.permissions)} replace />;
  }

  return <>{children}</>;
}

export function PublicOnly({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-center" style={{ minHeight: '100dvh' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (user) {
    return <Navigate to={getDefaultRoute(user.role, user.permissions)} replace />;
  }

  return <>{children}</>;
}
